import React, {
  useEffect,
  useState,
} from 'react';
import Style from './style';
import {
  Grid,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Tooltip,
  Typography,
} from '@mui/material';
import logo from '../CardView/logo';
import {
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  Constants,
  Interfaces,
} from '../../../config';
import {
  InfoOutlined,
  Loop,
  Save,
} from '@mui/icons-material';
import { DirectoryTree, } from '../DirectoryTree';
import LoadingComponent from '../../Loading';
import { NoDataV2, } from '../../NoDataV2';
import { useDSCalls, } from '../../../hooks';
import {
  StyledButton,
  StyledTextField,
} from '../../Reusable';


type Params = {
  oldName: string;
};

interface Props{
  schemaTypes: Array<string>;
}

function GoogleBucket(props: Props) {
  const classes = Style();
  const location = useLocation();
  const navigate = useNavigate();
  const inEditMode = location.pathname.includes('/edit');
  const { oldName, } = useParams<Params>();
  const { schemaTypes, } = props;
  const [exploreState, setExploreState,] = useState(true);

  const [state, setState,] = useState<Interfaces.GoogleBucketDataSource>({
    id: '',
    name: '',
    type: 'GCS',
    schemaType: schemaTypes[0],
    uri: '',
    serviceAccount: '',
  });

  const {
    dataSources,
    loading: loadingDS,
    editDataSource,
    addDataSource,
    dataSourceToEdit: dataSourceToEditHook,
    directories,
    loadingEdit,
    errorEdit,
    loadingDirectories,
    errorDirectories,
    fetchDirectories,
    setDataSourceToEdit,
    fetchNestedDirectory,
    setTestDataSource,
  } = useDSCalls();

  const dataSourceToEdit = dataSourceToEditHook as Interfaces.GoogleBucketDataSource| null;

  const endpoint = inEditMode && dataSourceToEdit
    ? `data-sources/${dataSourceToEdit.id}/gcs/dir`
    : Constants.api.endpoints.gcsDirectories;

  const fetchDirectoriesGB = (serviceAccount: string | null, uri: string | null) => {
    fetchDirectories({
      serviceAccount: serviceAccount,
      uri: uri,
    }, endpoint);
  };

  const [expandedElements, setExpandedElements,] = useState<Array<string>>([]);
  useEffect(() => {
    setTestDataSource(null);
    if (inEditMode) {
      if (dataSourceToEdit) {
        setState({
          ...state,
          id: dataSourceToEdit?.id,
          name: dataSourceToEdit?.name,
          type: dataSourceToEdit?.type,
          uri: dataSourceToEdit?.uri,
          schemaType: dataSourceToEdit?.schemaType,
          serviceAccount: null,
        });
        fetchDirectoriesGB(null, dataSourceToEdit?.uri);
        setExploreState(false);
      } else {
        if (!dataSources) {
          return;
        }
        const dataSourceToChange = dataSources.find((item) => item.name === oldName) as Interfaces.GoogleBucketDataSource;
        setDataSourceToEdit(dataSourceToChange);
      }
    }
  }, [dataSourceToEdit, dataSources,]);

  const handleChange = (event: any) => {
    let property = event.target.name;
    let value = event.target.value;

    setState({
      ...state,
      [property]: value,
    });
  };

  const postSubmitAction = () => {navigate('/data-sources');};

  const handleSubmit = (event: any) => {
    event.preventDefault();
    if (inEditMode && oldName) {
      editDataSource( state, oldName, postSubmitAction);
    } else {
      addDataSource(state, postSubmitAction);
    }
  };

  const getTypeFromName = (directory: Interfaces.Directory) => {
    if (directory.isDirectory) {
      return 'DIRECTORY';
    }
    const extension = directory.path.split('.').pop();
    if (extension === 'csv') {
      return 'CSV';
    }
    return 'AVRO';
  };

  const getTypeFromUri = (uri: string) => {
    const extension = uri.split('.').pop();
    if (extension === 'csv') {
      return 'CSV';
    } else if (extension === 'avro') {
      return 'AVRO';
    }
    return 'DIRECTORY';
  };

  if (loadingEdit) {
    return <LoadingComponent message={'Loading data source'}/>;
  }

  if (errorEdit) {
    return (
      <p>{errorEdit}</p>
    );
  }

  if (inEditMode && !dataSourceToEdit && !loadingDS) {
    return (
      <p>No data source was selected for edit mode</p>
    );
  }

  return (
    <form name="addGoogleBucketForm" onSubmit={handleSubmit}>
      <div className={classes.root}>
        <Grid container justifyContent='center'>
          <img
            src={logo.images.googleBucket}
            alt="Google Bucket"
            className={classes.img}
          />
        </Grid>
        <Grid container spacing={3} justifyContent='center'>
          <Grid item xs={6}>
            <StyledTextField
              id="name"
              name="name"
              label="Name"
              variant="outlined"
              value={state.name}
              fullWidth
              size="small"
              onChange={(event: any) => {
                handleChange(event);
              }}
            />
            <StyledTextField
              id="schemaType"
              name="schemaType"
              variant="outlined"
              value={state.schemaType}
              fullWidth
              size="small"
              onChange={handleChange}
              select
              SelectProps={{
                native: true,
              }}
            >
              {schemaTypes.map((option: string) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </StyledTextField>
            <StyledTextField
              id="serviceAccount"
              name="serviceAccount"
              label={state.serviceAccount !== null ? 'Service Account' : '(Service Account protected)'}
              multiline
              rows={8}
              value={state.serviceAccount}
              variant="outlined"
              fullWidth
              size="small"
              onChange={(event: any) => {
                handleChange(event);
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '4px',
              }}
            >
              <StyledTextField
                InputProps={{
                  endAdornment: (
                    <InputAdornment position={'end'}>
                      <Tooltip title={'Reload the buckets'} placement="top">
                        <IconButton
                          disabled={exploreState}
                          color={'primary'}
                          edge={'end'}
                          onClick={() => {
                            fetchDirectoriesGB(state.serviceAccount, state.uri === '' ? null : state.uri);
                            setState({
                              ...state,
                              schemaType: getTypeFromUri(state.uri),
                            });
                          }}
                        >
                          <Loop />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                id="uri"
                name="uri"
                label="Uri"
                variant="outlined"
                value={state.uri}
                fullWidth
                size="small"
                onChange={(event: any) => {
                  handleChange(event);
                }}
              />
              <Tooltip title="Uri must start with 'gs://'" placement="top">
                <InfoOutlined
                  fontSize="small"
                  style={{ marginLeft: 10, }}
                />
              </Tooltip>
            </div>
            {
              exploreState
                ? (
                  <Box className={classes.treeBoxEmpty}>
                    <StyledButton
                      disabled={!inEditMode && (state.serviceAccount === null || state.serviceAccount === '')}
                      onClick={() => {
                        fetchDirectoriesGB(state.serviceAccount, state.uri === '' ? null : state.uri);
                        setExploreState(false);
                      }}
                      color='primary'
                      variant='contained'
                      className={classes.exploreButton}
                    >
                      Explore
                    </StyledButton>
                    {
                      (!inEditMode && (state.serviceAccount === null || state.serviceAccount === ''))
                      && <Typography display={'block'} style={{ color: '#949494', }} variant={'caption'}>
                        Service account is required
                      </Typography>
                    }
                  </Box>
                )
                : (
                  <Box className={classes.treeBox}>
                    {loadingDirectories && directories.length === 0 && <LoadingComponent size={60} message={'Loading bucket'} />}
                    {
                      errorDirectories &&
                      <div style={{ textAlign: 'center', }}>
                        <NoDataV2 message={errorDirectories} />
                        <StyledButton
                          onClick={() => {
                            fetchDirectoriesGB(state.serviceAccount, state.uri === '' ? null : state.uri);
                            setExploreState(false);
                          }}
                          color='primary'
                          variant='contained'
                        >
                          Retry
                        </StyledButton>
                      </div>
                    }
                    {directories.length > 0 && !errorDirectories && (
                      <DirectoryTree
                        directories={directories}
                        isLoading={loadingDirectories}
                        expandedElements={expandedElements}
                        onTreeItemClick={(directory: Interfaces.Directory) => {
                          setState({
                            ...state,
                            uri: directory.path,
                            schemaType: getTypeFromName(directory),
                          });
                          if (directory.isDirectory) {
                            if (!directory.directories) {
                              fetchNestedDirectory({
                                serviceAccount: state.serviceAccount,
                                uri: directory.path,
                              }, endpoint, directory.path);
                            }
                            if (expandedElements.includes(directory.path)) {
                              setExpandedElements([...expandedElements,].filter((expanded: string) => expanded !== directory.path));
                            } else {
                              setExpandedElements([...expandedElements, directory.path,]);
                            }
                          }
                        }}/>
                    )}
                  </Box>
                )
            }
          </Grid>
        </Grid>
        <div style={{ width: '100%', }}>
          <StyledButton
            type="submit"
            style={{ marginLeft: 0, }}
            variant="contained"
            color="primary"
            size="large"
            startIcon={loadingDS ?
              <CircularProgress
                color="inherit"
                style={{
                  width: 16,
                  height: 11,
                }} />
              :
              <Save />}
          >
            Save
          </StyledButton>
          <StyledButton
            variant="outlined"
            size="large"
            className={classes.button}
            onClick={() => {
              navigate(-1);
            }}
          >
            Cancel
          </StyledButton>
        </div>
      </div>
    </form>
  );
}

export default GoogleBucket;
